import { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { find } from "lodash"
import moment from "moment"
import classnames from "classnames"
import { DATE, CHART_ITEM } from "helpers/shapes"
import { BACKGROUND_RECT, HIGHLIGHTER, MARK, LINE } from "../shapes"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import Tooltip from "components/charts/tooltip"
import Dot from "../dot"
import Axis, { AXIS } from "../helpers/axis"
import { getData, getLine, getBackgroundRect, getClickableRect, getTopMark, getBottomMark, getMinY, getMaxY, getCurrentMark, getCurrentValue, getScaleX, getScaleY, getHighlighter, getXAxis } from "./helper"
import "./style.scss"

class RangeLineChart extends Component {
  static propTypes = {
    tooltip: PropTypes.element.isRequired,
    line: LINE.isRequired,
    backgroundRect: BACKGROUND_RECT.isRequired,
    topMark: MARK.isRequired,
    bottomMark: MARK.isRequired,
    currentMark: MARK.isRequired,
    highlighter: HIGHLIGHTER,
    minY: PropTypes.number.isRequired,
    maxY: PropTypes.number.isRequired,
    scaleX: PropTypes.func.isRequired,
    scaleY: PropTypes.func.isRequired,
    currentValue: PropTypes.number,
    formatter: PropTypes.func.isRequired,
    scaleFormatter: PropTypes.func,
    data: PropTypes.arrayOf(CHART_ITEM).isRequired,
    id: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    startDate: DATE,
    endDate: DATE,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    clickableRect: PropTypes.object.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    date: DATE,
    setDate: PropTypes.func.isRequired,
    xAxis: AXIS,
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    const lookup = scaleX.invert(offset - 1) // -1 is to give a margin of selection on the first element
    return find(data, datum => datum.date >= lookup)
  }

  setDate = offset => {
    const { startDate, endDate, setDate } = this.props
    if (startDate !== endDate) {
      const { date } = this.findByOffset(offset)
      setDate(moment(date).format("YYYY-MM-DD"))
    }
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.pageX - box.left
    this.setDate(offsetX)
  }

  findDotCoordinates = offset => {
    const { scaleX, scaleY } = this.props
    const datum = this.findByOffset(offset)
    if (datum) {
      return {
        cx: scaleX(datum.date),
        cy: scaleY(datum.value),
      }
    } else {
      return null
    }
  }

  render() {
    const { tooltip, subscribe, unsubscribe, line, backgroundRect, clickableRect, topMark, bottomMark, color, minY, maxY, height, currentValue, currentMark, formatter, scaleFormatter = formatter, resizableRef, highlighter, data, xAxis } = this.props

    if (!data.length) {
      return <div ref={resizableRef}>No Data</div>
    } else {
      return <div>
        <svg ref={resizableRef} className="components_charts_range-line-chart" style={{ height }}>
          <g>
            <rect {...backgroundRect} />
            <Dot {...{ subscribe, unsubscribe, findDotCoordinates: this.findDotCoordinates, style: { fill: color } }} />
          </g>
          <Axis {...xAxis} />
          <rect {...clickableRect} className="range-line-background" onClick={this.handleGraphClick} />
          <path className={classnames("path", data.length === 1 && "path-single-dot")} {...line} />
          {highlighter && <line className="highlighter" {...highlighter} />}
          <text {...topMark} dy="1rem">{scaleFormatter(maxY)}</text>
          <text className="value" {...currentMark} dy="0.5rem">
            {formatter(currentValue)}
          </text>
          <text {...bottomMark} dy="-2px">{scaleFormatter(minY)}</text>
        </svg>
        <Tooltip {...{ subscribe, unsubscribe, findByOffset: this.findByOffset }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  data: getData(props),
  currentValue: getCurrentValue(props),
  line: getLine(props),
  backgroundRect: getBackgroundRect(props),
  clickableRect: getClickableRect(props),
  topMark: getTopMark(props),
  bottomMark: getBottomMark(props),
  currentMark: getCurrentMark(props),
  minY: getMinY(props),
  maxY: getMaxY(props),
  scaleX: getScaleX(props),
  scaleY: getScaleY(props),
  xAxis: getXAxis(props),
  highlighter: getHighlighter(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(RangeLineChart)))
